import { useState, type ComponentPropsWithoutRef, type ReactNode } from 'react'

import { formatMoney, normalizeMoneyString, sanitizeMoneyInput } from '@/lib/money'
import { Input } from '@/components/ui/Input'
import { cn } from '@/lib/utils'

export type MoneyInputProps = Omit<
  ComponentPropsWithoutRef<'input'>,
  'id' | 'type' | 'value' | 'onChange' | 'inputMode'
> & {
  id: string
  label?: ReactNode
  error?: string | null
  /** Canonical decimal string without grouping, e.g. "1234.5" */
  value: string
  onChange: (next: string) => void
  currencyCode?: string
  className?: string
  inputClassName?: string
}

export function MoneyInput({
  id,
  label,
  error = null,
  value,
  onChange,
  currencyCode = 'USD',
  className = '',
  inputClassName = '',
  disabled = false,
  required = false,
  ...rest
}: MoneyInputProps) {
  const [focused, setFocused] = useState(false)

  const numeric = value === '' ? NaN : Number(value)
  const hasNumber = value !== '' && !Number.isNaN(numeric) && Number.isFinite(numeric)

  const display = focused
    ? value
    : hasNumber
      ? formatMoney(numeric, currencyCode)
      : ''

  return (
    <Input
      id={id}
      label={label}
      error={error}
      disabled={disabled}
      className={className}
      inputClassName={cn('tabular-nums', inputClassName)}
      value={display}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false)
        const next = normalizeMoneyString(value)
        onChange(next)
      }}
      onChange={(e) => onChange(sanitizeMoneyInput(e.target.value))}
      inputMode="decimal"
      autoComplete="off"
      required={required}
      {...rest}
    />
  )
}
